import {
  buildDynamicConvoStarterAvailability,
  getEligibleDynamicConvoStarters,
  packChipsIntoTwoRows,
  selectVisibleDynamicConvoStarters,
  type StarterChip,
} from "./selectDynamicConvoStarters";
import {
  logCareerScoutProductButtonTap,
  logCareerScoutProductImpression,
} from "../../../../../ace/logging/signals";
import { AILogo, Box, Pressable, ScrollView, Text } from "../../../../../mads";
import { useI18n } from "../../../../../shared/I18n";
import {
  encodeViewjobStarterSourceContext,
  getConversationForStarter,
  getConversationForViewjob,
  setActiveViewjobJobKey,
} from "../../../../../shared/lib/conversationStarter/ConversationStarterStorage";
import { JSTModalScreen } from "../../../../../shared/native-modules/tare/JSTModalScreen";
import { openModal } from "../../../../../shared/native-modules/tare/openModal";
import { useRunOnceAfterMount } from "../../../../../shared/util/useRunOnceAfterMount";
import { useViewjobOneGraphModel } from "../../../context/ViewJobDataContext";

import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

interface DynamicConvoStartersProps {
  horizontalBleedInset?: number;
}

export const DynamicConvoStarters: React.FC<DynamicConvoStartersProps> = ({
  horizontalBleedInset,
}) => {
  const gt = useI18n();
  const ogModel = useViewjobOneGraphModel();
  const jobKey = ogModel?.job.jobKey;
  const jobTitle = ogModel?.job.jobTitle;
  const companyName = ogModel?.employer?.companyName;

  const availability = useMemo(() => buildDynamicConvoStarterAvailability(ogModel), [ogModel]);

  // Reshuffle when gated availability inputs change so the visible set stays
  // stable across unrelated re-renders of the sheet.
  const chips = useMemo(() => {
    const eligible = getEligibleDynamicConvoStarters(availability);
    return selectVisibleDynamicConvoStarters(eligible);
  }, [availability]);

  const rows = useMemo(() => packChipsIntoTwoRows(chips), [chips]);
  const chipCount = chips.length;

  // Shared across every chip so screen readers announce them as one group.
  const groupHint = gt.gettext("{0} group", [gt.gettext("Ask Career Scout")]);

  const handlePress = useCallback(
    (label: string, message: string) => {
      const existingConversationId = jobKey ? getConversationForViewjob(jobKey) : null;

      if (jobKey) {
        setActiveViewjobJobKey(jobKey);
      }

      const starterAlreadySent = jobKey
        ? getConversationForStarter(message, jobKey) !== null
        : false;
      const starterMessage = starterAlreadySent ? null : message;

      const sourceContext = jobKey
        ? encodeViewjobStarterSourceContext(jobKey, message, {
            jobTitle,
            companyName,
          })
        : "viewjob";

      logCareerScoutProductButtonTap({
        elementName: "askCareerScoutDynamic",
        assistantMessageId: null,
        from: "viewJob",
        jobKey: jobKey ?? undefined,
        suggestion: label,
      });

      void openModal(
        JSTModalScreen.aceConversation({
          starterMessage,
          conversationId: existingConversationId,
          sourceContext,
        }),
        "viewjob",
      );
    },
    [jobKey, jobTitle, companyName],
  );

  useRunOnceAfterMount(() => {
    logCareerScoutProductImpression({
      elementName: "askCareerScoutDynamic",
      assistantMessageId: null,
      from: "viewJob",
      jobKey: jobKey ?? undefined,
      suggestions: chips.map((chip) => chip.label).join(", "),
    });
  });

  const renderChip = (chip: StarterChip) => {
    const label = gt.gettext(chip.label);
    const message = gt.gettext(chip.message ?? chip.label);
    // The chips wrap across two rows inside a horizontal scroller, so visual
    // order doesn't convey position. Announce it explicitly.
    const position = chips.indexOf(chip) + 1;
    return (
      <Pressable
        key={chip.id}
        onPress={() => handlePress(label, message)}
        sx={({ pressed }) => [styles.chip, pressed && styles.chipPressed]}
        accessible
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={gt.gettext("{0}, {1} of {2}", [
          groupHint,
          String(position),
          String(chipCount),
        ])}
      >
        {/* The AI logo is decorative; keep each chip a single a11y node. */}
        <Box
          sx={styles.chipContent}
          testID="ask-career-scout-dynamic-convo-starter-chip-content"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <AILogo size="sm" />
          <Text level={2} sx={styles.chipText} selectable={false}>
            {label}
          </Text>
        </Box>
      </Pressable>
    );
  };

  const bleedInset = horizontalBleedInset && horizontalBleedInset > 0 ? horizontalBleedInset : 0;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      testID="ask-career-scout-dynamic-convo-starters-scroll"
      sx={bleedInset ? { marginStart: -bleedInset, marginEnd: -bleedInset } : undefined}
      contentContainerSx={[
        styles.scrollContent,
        bleedInset ? { paddingStart: bleedInset, paddingEnd: bleedInset } : undefined,
      ]}
    >
      {rows.map((row, rowIndex) => (
        <Box key={row.map((chip) => chip.id).join("|") || `row-${rowIndex}`} sx={styles.row}>
          {row.map(renderChip)}
        </Box>
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: spacing.s2,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.s2,
  },
  chip: {
    alignSelf: "flex-start",
    flexShrink: 1,
    borderWidth: 1,
    borderColor: colors.borderWeak,
    borderRadius: borderRadius.lg,
    paddingHorizontal: spacing.s4,
    paddingVertical: spacing.s2,
    minHeight: 42,
    backgroundColor: colors.background,
    maxWidth: 338,
    justifyContent: "center",
  },
  chipPressed: {
    backgroundColor: colors.backgroundActionActive,
    borderColor: colors.borderActionActive,
  },
  chipContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.s2,
  },
  chipText: {
    color: colors.foreground,
  },
});
